import React from 'react';
import { contactTitle } from '@/lib/contactName';
import { API_BASE, api } from '@/lib/api';
import { playChime, unlockAudioOnce } from '@/lib/chime';
import { Bell, BellOff, MonitorDot, MonitorOff } from 'lucide-react';
import { toast } from 'sonner';
import { MessageBanner, StackBanner } from '@/components/MessageBanner';
import { useAuth } from '@/lib/auth';

const LiveCtx = React.createContext({ status: 'offline', subscribe: () => () => {}, unread: { total_unread: 0 }, refreshUnread: () => {}, muted: false, toggleMute: () => {} });
const MUTE_KEY = 'verse_live_muted';
const DESKTOP_KEY = 'verse_desktop_alerts';
const PROMPT_KEY = 'verse_desktop_prompted';
const RECENT_KEY = 'verse_recent_chats';
const STACK_AT = 3;  // from the 3rd person, pop-ups fold into one card
const LEAD_MS = 10000;
const notifSupported = () => typeof window !== 'undefined' && 'Notification' in window;

const showDesktopAlert = (ev) => {
  const n = new Notification(ev.kind === 'new_lead' ? `New lead: ${contactTitle(ev)}` : `New message from ${contactTitle(ev)}`, {
    body: previewText(ev.preview) || ev.channel || '', tag: `verse-${ev.customer_id}`, icon: '/favicon.ico',
  });
  n.onclick = () => { window.focus(); n.close(); openLead(ev.customer_id); };
};
const isIncoming = (ev) => ev.kind === 'message' || ev.kind === 'new_lead';
// Phones and tablets held upright: top centre (like iOS). Landscape tablets and desktops: top right (like macOS).
const bannerPosition = () => (window.matchMedia('(orientation: landscape) and (min-width: 640px)').matches ? 'top-right' : 'top-center');
const PREVIEW = { image: 'Photo', photo: 'Photo', sticker: 'Sticker', audio: 'Voice note', voice: 'Voice note', video: 'Video',
  file: 'File', document: 'File', attachment: 'Attachment', share: 'Shared post', story_mention: 'Story mention', ig_reel: 'Reel', reel: 'Reel', unsupported_type: 'Shared a post or reel' };
export const previewText = (t) => {
  const m = /^\((\w+)\)$/.exec((t || '').trim());
  if (/^(📎 Unsupported_Type|🔗 Shared a post or reel)$/i.test((t || '').trim())) return PREVIEW.unsupported_type;
  return m ? (PREVIEW[m[1].toLowerCase()] || 'Attachment') : t;
};
// The chat currently open in the Inbox: no pop-up or sound for it while the tab is visible.
let openChat = null;
export const setOpenChat = (id) => { openChat = id; };
const isViewing = (ev) => ev.customer_id && ev.customer_id === openChat && !document.hidden;
export const openInboxChat = (id) => openLead(id);
const openLead = (id) => {
  if (window.location.pathname.startsWith('/vc/inbox')) window.dispatchEvent(new CustomEvent('verse:open-inbox', { detail: id }));
  else { window.history.pushState(null, '', `/vc/inbox?c=${id}`); window.dispatchEvent(new PopStateEvent('popstate')); }  // in-app, no reload
};

const openInbox = () => { window.history.pushState(null, '', '/vc/inbox'); window.dispatchEvent(new PopStateEvent('popstate')); };

// Recent chat alerts for the bell (one row per person, newest first, kept on this device).
const addRecent = (list, ev) => {
  const old = list.find((x) => x.customer_id === ev.customer_id);
  const row = { customer_id: ev.customer_id, title: contactTitle(ev), text: previewText(ev.preview) || `New ${ev.channel || ''} message`,
    channel: ev.channel, lead: ev.kind === 'new_lead' || !!old?.lead, count: (old && !old.read ? old.count : 0) + 1, at: ev.at || new Date().toISOString(), read: false };
  return [row, ...list.filter((x) => x.customer_id !== ev.customer_id)].slice(0, 50);
};
const useRecentChats = () => {
  const [recent, set] = React.useState(() => { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; } });
  React.useEffect(() => { localStorage.setItem(RECENT_KEY, JSON.stringify(recent)); }, [recent]);
  return [recent, set];
};

const useNameRecoveryToasts = () => {
  const batch = React.useRef([]);
  const timer = React.useRef(null);
  return React.useCallback((ev) => {
    batch.current.push(ev);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const items = batch.current; batch.current = [];
      if (items.length === 1) {
        toast.success(`Name found: ${items[0].display_name}`, { description: 'Recovered from Meta', action: { label: 'Open', onClick: () => openLead(items[0].customer_id) } });
      } else {
        toast.success(`${items.length} names recovered from Meta`, { description: items.slice(0, 3).map((i) => i.display_name).join(', ') + (items.length > 3 ? '…' : ''), action: { label: 'View leads', onClick: () => window.location.assign('/vc/leads') } });
      }
    }, 1500);
  }, []);
};

const useEventSource = (onEvent, setStatus) => {
  React.useEffect(() => {
    let es; let retry; let stopped = false; let lastAt = null; let dropped = false;
    const connect = () => {
      const token = localStorage.getItem('verse_token');
      if (!token || stopped) return;
      setStatus(dropped ? 'reconnecting' : 'connecting');
      // On reconnect, resume from the last event so anything that arrived during the drop still alerts.
      const since = dropped && lastAt ? `&since=${encodeURIComponent(lastAt)}` : '';
      es = new EventSource(`${API_BASE}/events/stream?token=${encodeURIComponent(token)}${since}`);
      es.onmessage = (m) => {
        let ev; try { ev = JSON.parse(m.data); } catch { return; }
        if (ev.at) lastAt = ev.at;
        if (ev.kind === 'hello') {
          setStatus('live');
          if (dropped) { dropped = false; onEvent({ kind: 'resync', at: ev.at }); }
          return;
        }
        onEvent(ev);
      };
      es.onerror = () => {
        es.close(); dropped = true;
        setStatus('reconnecting');
        retry = setTimeout(connect, 3000);
      };
    };
    connect();
    return () => { stopped = true; clearTimeout(retry); es?.close(); };
  }, [onEvent, setStatus]);
};

export function LiveEventsProvider({ children }) {
  const [status, setStatus] = React.useState('connecting');
  const [unread, setUnread] = React.useState({ total_unread: 0, contacts_unread: 0 });
  const [muted, setMuted] = React.useState(() => localStorage.getItem(MUTE_KEY) === '1');
  const mutedRef = React.useRef(muted);
  mutedRef.current = muted;
  const [desktop, setDesktop] = React.useState(() => notifSupported() && Notification.permission === 'granted' && localStorage.getItem(DESKTOP_KEY) === '1');
  const desktopRef = React.useRef(desktop);
  desktopRef.current = desktop;
  const listeners = React.useRef(new Set());

  const refreshUnread = React.useCallback(async () => {
    try { const { data } = await api.get('/unread-summary'); setUnread(data); } catch { /* ignore */ }
  }, []);
  React.useEffect(() => { refreshUnread(); unlockAudioOnce(); }, [refreshUnread]);

  const nameToast = useNameRecoveryToasts();
  const { user } = useAuth();
  const meRef = React.useRef(null);
  meRef.current = user?.name || null;
  const [recent, setRecent] = useRecentChats();
  const lastChime = React.useRef(0);
  const piles = React.useRef({});  // customer_id -> { count, lead, position } while their own pop-up is showing
  const stack = React.useRef(null);  // { people: Map(cid -> name), count } while the folded card is showing
  const showStack = React.useCallback(() => {
    const st = stack.current;
    const done = () => { stack.current = null; };
    toast.custom(() => (
      <StackBanner people={[...st.people.values()]} count={st.count} leads={st.leads}
        onClose={() => { toast.dismiss('msg-stack'); done(); }} onOpen={() => { toast.dismiss('msg-stack'); done(); openInbox(); }} />
    ), { id: 'msg-stack', position: st.position, duration: LEAD_MS, unstyled: true, onDismiss: done, onAutoClose: done });
  }, []);
  const popUp = React.useCallback((ev) => {
    const cid = ev.customer_id;
    const lead = ev.kind === 'new_lead';
    if (stack.current || (!piles.current[cid] && Object.keys(piles.current).length >= STACK_AT - 1)) {
      if (!stack.current) {  // fold the open pop-ups into one card
        const open = Object.entries(piles.current);
        stack.current = { people: new Map(open.map(([k, p]) => [k, p.name])), count: open.reduce((n, [, p]) => n + p.count, 0),
          leads: open.filter(([, p]) => p.lead).length, position: open[0]?.[1].position || bannerPosition() };
        open.forEach(([k]) => toast.dismiss(`msg-${k}`));
        piles.current = {};
      }
      const st = stack.current;
      if (lead && !st.people.has(cid)) st.leads += 1;
      st.people.set(cid, contactTitle(ev));
      st.count += 1;
      showStack();
      return;
    }
    const id = `msg-${cid}`;
    const prev = piles.current[cid];
    const pile = { count: (prev?.count || 0) + 1, lead: !!prev?.lead || lead, name: contactTitle(ev), position: prev?.position || bannerPosition() };
    piles.current[cid] = pile;
    const done = () => { delete piles.current[cid]; };
    const close = () => { toast.dismiss(id); done(); };
    toast.custom(() => (
      <MessageBanner ev={ev} count={pile.count} sticky={pile.lead} title={pile.lead ? `New lead · ${contactTitle(ev)}` : contactTitle(ev)}
        text={previewText(ev.preview) || `New ${ev.channel || ''} message`} onClose={close} onOpen={() => { close(); openLead(cid); }} />
    ), { id, position: pile.position, duration: pile.lead ? LEAD_MS : 6000, unstyled: true, onDismiss: done, onAutoClose: done });
  }, [showStack]);
  const onEvent = React.useCallback((ev) => {
    if (ev.kind === 'name_recovered') nameToast(ev);
    if (ev.kind === 'resync') refreshUnread();
    if (isIncoming(ev)) {
      refreshUnread();
      // Pop-ups only for my chats; unassigned chats alert everyone
      if (!isViewing(ev) && (!ev.assigned_staff || ev.assigned_staff === meRef.current)) {
        if (!mutedRef.current && Date.now() - lastChime.current > 1500) { lastChime.current = Date.now(); playChime(); }
        setRecent((r) => addRecent(r, ev));
        popUp(ev);
        if (desktopRef.current && document.hidden && notifSupported() && Notification.permission === 'granted') {
          try { showDesktopAlert(ev); } catch { /* ignore */ }
        }
      }
    }
    listeners.current.forEach((fn) => fn(ev));
  }, [refreshUnread, nameToast, popUp, setRecent]);
  useEventSource(onEvent, setStatus);

  const subscribe = React.useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);
  const toggleMute = React.useCallback(() => setMuted((m) => {
    localStorage.setItem(MUTE_KEY, m ? '0' : '1');
    return !m;
  }), []);

  const toggleDesktop = React.useCallback(async () => {
    if (!notifSupported()) return 'unsupported';
    if (desktop) { localStorage.setItem(DESKTOP_KEY, '0'); setDesktop(false); return 'off'; }
    const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (perm !== 'granted') return perm;
    localStorage.setItem(DESKTOP_KEY, '1'); setDesktop(true); return 'on';
  }, [desktop]);

  React.useEffect(() => {  // one-time nudge to turn on desktop alerts
    if (!notifSupported() || desktop || Notification.permission === 'denied' || localStorage.getItem(PROMPT_KEY)) return undefined;
    const t = setTimeout(() => toast('Get an alert for every new message?', {
      id: 'desktop-alerts-prompt', duration: Infinity, description: 'Shows a desktop notification when the CRM tab is in the background.',
      action: { label: 'Turn on', onClick: () => { localStorage.setItem(PROMPT_KEY, '1'); toggleDesktop(); } },
      cancel: { label: 'Not now', onClick: () => localStorage.setItem(PROMPT_KEY, '1') },
    }), 4000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = React.useMemo(() => ({ status, subscribe, unread, refreshUnread, muted, toggleMute, desktop, toggleDesktop, recent, setRecent }),
    [status, subscribe, unread, refreshUnread, muted, toggleMute, desktop, toggleDesktop, recent, setRecent]);
  return <LiveCtx.Provider value={value}>{children}</LiveCtx.Provider>;
}

export const useLive = () => React.useContext(LiveCtx);

export function useLiveEvent(handler) {
  const { subscribe } = useLive();
  const ref = React.useRef(handler);
  ref.current = handler;
  React.useEffect(() => subscribe((ev) => ref.current(ev)), [subscribe]);
}

/** Debounced refresh on any live event so busy moments don't flicker the screen. */
export function useLiveRefresh(fn, ms = 1000) {
  const ref = React.useRef(fn);
  ref.current = fn;
  const timer = React.useRef(null);
  React.useEffect(() => () => clearTimeout(timer.current), []);
  useLiveEvent((ev) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => ref.current(ev), ms);
  });
}

export function LiveDot() {
  const { status } = useLive();
  const live = status === 'live';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${live ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}
      data-testid="live-status-dot" data-status={status}>
      <span className="relative flex h-2 w-2">
        {live && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${live ? 'bg-emerald-500' : 'bg-amber-500'}`} />
      </span>
      {live ? 'Live' : status === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
    </span>
  );
}

export function MuteToggle() {
  const { muted, toggleMute } = useLive();
  const Icon = muted ? BellOff : Bell;
  return (
    <button type="button" onClick={() => { toggleMute(); if (muted) playChime(); }}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${muted ? 'border-slate-300 bg-white text-slate-500' : 'border-[#E7D7B8] bg-[#EFEFF2] text-[#2C2C2E]'}`}
      data-testid="live-mute-toggle" data-muted={muted ? 'true' : 'false'} aria-pressed={muted}
      title={muted ? 'Sound off — click to turn on' : 'Sound on — click to mute'}>
      <Icon className="h-3.5 w-3.5" />{muted ? 'Muted' : 'Sound on'}
    </button>
  );
}

export function UnreadBadge({ count, testid, className = '' }) {
  if (!count) return null;
  return (
    <span className={`inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-[#C7A46A] px-1.5 text-[10px] font-semibold text-[#1D1D1F] tabular-nums ${className}`} data-testid={testid}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function DesktopAlertToggle() {
  const { desktop, toggleDesktop } = useLive();
  const [msg, setMsg] = React.useState('');
  if (!notifSupported()) return null;
  const Icon = desktop ? MonitorDot : MonitorOff;
  const click = async () => {
    const r = await toggleDesktop();
    setMsg(r === 'denied' ? 'Blocked in browser settings' : '');
  };
  return (
    <button type="button" onClick={click} title={msg || (desktop ? 'Desktop alerts on (shown when this tab is in the background)' : 'Turn on desktop alerts')}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${desktop ? 'border-[#E7D7B8] bg-[#EFEFF2] text-[#2C2C2E]' : 'border-slate-300 bg-white text-slate-500'}`}
      data-testid="desktop-alert-toggle" data-enabled={desktop ? 'true' : 'false'}>
      <Icon className="h-3.5 w-3.5" />{msg || (desktop ? 'Desktop alerts on' : 'Desktop alerts off')}
    </button>
  );
}

const fmtWait = (mins) => (mins < 60 ? `${mins}m` : mins < 1440 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`);

export function SpeedTimer({ task, testid }) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!task || !['open', 'escalated'].includes(task.status)) return null;
  const secs = Math.max(0, Math.floor((now - new Date(task.created_at).getTime()) / 1000));
  const overdue = now >= new Date(task.due_at).getTime();
  const escalated = task.status === 'escalated';
  const cls = escalated ? 'bg-red-600 text-white border-red-600 animate-pulse' : overdue ? 'bg-red-50 text-red-700 border-red-300' : secs >= 120 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  const mm = Math.floor(secs / 60);
  const label = mm >= 60 ? `${Math.floor(mm / 60)}h ${mm % 60}m` : `${mm}:${String(secs % 60).padStart(2, '0')}`;
  return (
    <span className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-medium tabular-nums ${cls}`} data-testid={testid}
      data-state={escalated ? 'escalated' : overdue ? 'overdue' : 'ok'} title={task.assigned_to ? `Assigned to ${task.assigned_to}` : 'Unassigned'}>
      {escalated ? 'ESCALATED ' : 'Call '}{label}
    </span>
  );
}

export function WaitingChip({ since, testid }) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => { const t = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(t); }, []);
  if (!since) return null;
  const mins = Math.max(0, Math.floor((now - new Date(since).getTime()) / 60000));
  const late = mins >= 60;
  return (
    <span className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-medium tabular-nums ${late ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`}
      data-testid={testid} data-late={late ? 'true' : 'false'}>
      Waiting {fmtWait(mins)}
    </span>
  );
}
